import * as action from '../action-types'
import * as mutation from '../mutation-types'

const CHANNEL_ID = {
  PREVIEW: 'preview',
  PROGRAM: 'program'
}

export const state = {
  overlays: []
}

export const getters = {
  isPlaying: (state, getters) => (templateId, channelId) => {
    const template = getters.template(templateId)
    if (!template) return false
    const layer = template.layer
    const overlay = state.overlays.find(o => o.channelId === channelId && o.layer === layer)
    return overlay && overlay.playing
  },
  overlay: (state) => (channelId, layer) => {
    return state.overlays.find(o => o.channelId === channelId && o.layer === layer)
  }
}

export const actions = {
  [action.CLEAR_CHANNEL] ({commit}, {channelId}) {
    commit(mutation.CHANNEL_CLEAR, {channelId, emit: true})
  },

  [action.TOGGLE_PLAY_STOP] ({getters, commit}, {channelId, templateId}) {
    if (typeof channelId !== 'string' || !channelId) throw TypeError('channelId must be a string')
    if (typeof templateId !== 'string' || !templateId) throw TypeError('templateId must be a string')
    const template = getters.template(templateId)
    if (!template) throw Error('template not found')
    const layer = template.layer
    const overlay = getters.overlay(channelId, layer)
    if (overlay && overlay.playing) {
      commit(mutation.OVERLAY_STOP, {
        fileName: template.overlayFileName, channelId, layer, emit: true
      })
    } else {
      const data = getters.overlayData(templateId)
      const options = getters.overlayOptions(templateId)
      if (overlay) {
        commit(mutation.OVERLAY_PLAY, {
          fileName: template.overlayFileName, channelId, layer, data, options, emit: true
        })
      } else {
        commit(mutation.OVERLAY_ADD, {
          fileName: template.overlayFileName, channelId, layer, data, options, emit: true
        })
      }
      // "Take" from preview to program
      if (channelId === CHANNEL_ID.PROGRAM) {
        commit(mutation.OVERLAY_STOP, {
          fileName: template.overlayFileName, channelId: CHANNEL_ID.PREVIEW, layer, emit: true
        })
      }
    }
  },

  [action.COMMAND_NEXT] ({getters, commit}, {channelId, templateId}) {
    if (typeof channelId !== 'string' || !channelId) throw TypeError('channelId must be a string')
    if (typeof templateId !== 'string' || !templateId) throw TypeError('templateId must be a string')
    const template = getters.template(templateId)
    if (!template) throw Error('template not found')
    const layer = template.layer
    const overlay = getters.overlay(channelId, layer)
    if (!overlay || !overlay.playing) return
    commit(mutation.OVERLAY_NEXT, {
      channelId,
      layer: template.layer,
      emit: true
    })
  },

  [action.COMMAND_INVOKE] ({getters, commit}, {channelId, templateId, func}) {
    if (typeof channelId !== 'string' || !channelId) throw TypeError('channelId must be a string')
    if (typeof templateId !== 'string' || !templateId) throw TypeError('templateId must be a string')
    const template = getters.template(templateId)
    if (!template) throw Error('template not found')
    const layer = template.layer
    const overlay = getters.overlay(channelId, layer)
    if (!overlay || !overlay.playing) return
    commit(mutation.OVERLAY_INVOKE, {
      channelId,
      layer: template.layer,
      func,
      emit: true
    })
  },

  [action.COMMAND_UPDATE] ({getters, commit}, {channelId, templateId}) {
    if (typeof channelId !== 'string' || !channelId) throw TypeError('channelId must be a string')
    if (typeof templateId !== 'string' || !templateId) throw TypeError('templateId must be a string')
    const template = getters.template(templateId)
    if (!template) throw Error('template not found')
    const layer = template.layer
    const overlay = getters.overlay(channelId, layer)
    if (!overlay || !overlay.playing) return
    const data = getters.overlayData(templateId)
    commit(mutation.OVERLAY_DATA, {
      channelId,
      layer: template.layer,
      data,
      emit: true
    })
  },

  [action.OVERLAY_DATA] ({getters, commit}, {templateId, channelIds, data}) {
    if (typeof templateId !== 'string' || !templateId) throw TypeError('templateId must be a string')
    channelIds = Array.isArray(channelIds) ? channelIds : [channelIds]
    const template = getters.template(templateId)
    if (!template) throw Error('template not found')
    data = data || getters.overlayData(templateId)
    channelIds.forEach((channelId) => {
      commit(mutation.OVERLAY_DATA, {
        channelId,
        layer: template.layer,
        data,
        emit: true
      })
    })
  },

  [action.OVERLAY_OPTIONS] ({getters, commit}, {templateId, channelIds, options}) {
    if (typeof templateId !== 'string' || !templateId) throw TypeError('templateId must be a string')
    channelIds = Array.isArray(channelIds) ? channelIds : [channelIds]
    const template = getters.template(templateId)
    if (!template) throw Error('template not found')
    options = options || getters.templateOptions(templateId)
    channelIds.forEach((channelId) => {
      commit(mutation.OVERLAY_OPTIONS, {
        channelId,
        layer: template.layer,
        options,
        emit: true
      })
    })
  }
}

export const mutations = {
  [mutation.CHANNEL_CLEAR] (state, {channelId}) {
    if (typeof channelId !== 'string') throw TypeError('channelId must be a string')
    for (let idx = state.overlays.length - 1; idx >= 0; idx--) {
      if (state.overlays[idx].channelId === channelId) {
        state.overlays.splice(idx, 1)
      }
    }
  },

  [mutation.CHANNEL_STATE] (state, {channelId, overlays}) {
    overlays.forEach(each => {
      let overlay = state.overlays.find(o => o.channelId === each.channelId && o.layer === each.layer)
      if (!overlay) {
        overlay = {
          channelId: channelId,
          layer: each.layer,
          overlayFileName: each.overlayFileName,
          playing: !!each.playing,
          options: each.options,
          data: each.options
        }
        state.overlays.push(overlay)
      }
    })
  },

  [mutation.OVERLAY_ADD] (state, {channelId, layer, fileName, data, options, playing}) {
    if (typeof channelId !== 'string' || !channelId) throw TypeError('channelId must be a string')
    if (typeof layer !== 'number') throw TypeError('layer must be a number')
    if (typeof fileName !== 'string' || !fileName) throw TypeError('fileName must be a string')
    data = data || {}
    options = options || {}
    let overlay = state.overlays.find(o => o.channelId === channelId && o.layer === layer)
    if (!overlay) {
      overlay = {channelId, layer, fileName, playing: !!playing, options, data}
      state.overlays.push(overlay)
    } else {
      overlay.playing = true
      overlay.data = Object.assign({}, data)
      overlay.options = Object.assign({}, options)
    }
  },

  [mutation.OVERLAY_REMOVE] (state, {channelId, layer}) {
    if (typeof channelId !== 'string') throw TypeError('channelId must be a string')
    if (typeof layer !== 'number') throw TypeError('layer must be a number')
    const idx = state.overlays.findIndex(o => o.channelId === channelId && o.layer === layer)
    if (idx < 0) return
    state.overlays.splice(idx, 1)
  },

  [mutation.OVERLAY_PLAY] (state, {channelId, layer}) {
    if (typeof channelId !== 'string') throw TypeError('channelId must be a string')
    if (typeof layer !== 'number') throw TypeError('layer must be a number')
    const overlay = state.overlays.find(o => o.channelId === channelId && o.layer === layer)
    if (!overlay) return
    overlay.playing = true
  },

  [mutation.OVERLAY_STOP] (state, {channelId, layer}) {
    if (typeof channelId !== 'string') throw TypeError('channelId must be a string')
    if (typeof layer !== 'number') throw TypeError('layer must be a number')
    const overlay = state.overlays.find(o => o.channelId === channelId && o.layer === layer)
    if (!overlay) return
    overlay.playing = false
  },

  [mutation.OVERLAY_DATA] (state, {channelId, layer, data}) {
    if (typeof channelId !== 'string') throw TypeError('channelId must be a string')
    if (typeof layer !== 'number') throw TypeError('layer must be a number')
    if (typeof data !== 'object') throw TypeError('data must be an object')
    const overlay = state.overlays.find(o => o.channelId === channelId && o.layer === layer)
    if (!overlay) return
    overlay.data = Object.assign({}, overlay.data, data)
  },

  [mutation.OVERLAY_OPTIONS] (state, {channelId, layer, options}) {
    if (typeof channelId !== 'string') throw TypeError('channelId must be a string')
    if (typeof layer !== 'number') throw TypeError('layer must be a number')
    if (typeof options !== 'object') throw TypeError('options must be an object')
    const overlay = state.overlays.find(o => o.channelId === channelId && o.layer === layer)
    if (!overlay) return
    overlay.options = Object.assign({}, overlay.options, options)
  },

  [mutation.OVERLAY_NEXT] (state, {channelId, layer}) {
    // Noop
  },

  [mutation.OVERLAY_INVOKE] (state, {channelId, layer, func}) {
    // Noop
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/channels.js